/**
 * Auth store — minimal Zustand store for JWT access token and session identity.
 *
 * Persisted to sessionStorage so auth survives page refreshes but clears on
 * tab close (preventing stale tokens across browser sessions).
 *
 * Integration point for BOLT-018 portal login: call setAuth() after a
 * successful /v1/auth/login response, clearAuth() on logout.
 *
 * The api-client reads accessToken via useAuthStore.getState().accessToken
 * to inject the Authorization header without subscribing to re-renders.
 *
 * BOLT-025: targetEmail holds the impersonated user's email for the banner.
 * setAccessToken() updates only the token (used by impersonation silent-refresh).
 * setTargetEmail() sets/clears the impersonation target email.
 *
 * BOLT-403: impersonationSessionId is THE tab-scoping mechanism. It lives in
 * sessionStorage (per-tab), names which impersonation session this tab holds,
 * and routes the tab's silent refresh to /v1/auth/refresh-impersonation.
 * A tab without one refreshes as the real human — so one tab's impersonation
 * can never leak into another tab's identity.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Role values that the Portal uses for client-side route/nav gating.
 *
 * Note: client-side checks are advisory only — every API endpoint enforces RBAC
 * on the server (per BOLT-020 middleware). The Portal mirrors role here so the
 * Admin sidebar section and `<RequireRole>` redirect can avoid mounting routes
 * that the user has no business seeing.
 */
export type UserRole = 'super_admin' | 'org_owner' | 'org_admin' | 'staff' | 'viewer';

interface AuthState {
  accessToken: string | null;
  orgId: string | null;
  userId: string | null;
  /** Role copied from the JWT payload after login (BOLT-018 integration point) */
  role: UserRole | null;
  /** Email of the impersonated user — non-null only during an impersonation session (BOLT-025) */
  targetEmail: string | null;
  /** Impersonation session this TAB holds — non-null only while impersonating (BOLT-403) */
  impersonationSessionId: string | null;
  setAuth: (accessToken: string, orgId: string, userId: string, role: UserRole) => void;
  /** Updates only the access token without touching identity fields — used by impersonation silent-refresh (BOLT-025) */
  setAccessToken: (accessToken: string) => void;
  /** Sets or clears the impersonated user's email for the ImpersonationBanner (BOLT-025) */
  setTargetEmail: (email: string | null) => void;
  /** Sets or clears the tab's impersonation session id (BOLT-403) */
  setImpersonationSessionId: (sessionId: string | null) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      orgId: null,
      userId: null,
      role: null,
      targetEmail: null,
      impersonationSessionId: null,
      setAuth: (accessToken, orgId, userId, role) => set({ accessToken, orgId, userId, role }),
      setAccessToken: (accessToken) => set({ accessToken }),
      setTargetEmail: (email) => set({ targetEmail: email }),
      setImpersonationSessionId: (sessionId) => set({ impersonationSessionId: sessionId }),
      clearAuth: () =>
        set({
          accessToken: null,
          orgId: null,
          userId: null,
          role: null,
          targetEmail: null,
          impersonationSessionId: null,
        }),
    }),
    {
      name: 'crm98-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        orgId: state.orgId,
        userId: state.userId,
        role: state.role,
        targetEmail: state.targetEmail,
        impersonationSessionId: state.impersonationSessionId,
      }),
    },
  ),
);
